import { HTMLAttributes, useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'

import Typography from '@/components/base/Typography'

import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type CtaProps = HTMLAttributes<HTMLElement> &
  Partial<{
    theme?: 'dark' | 'light'
    link: WPLink
    className?: string
  }>

const Cta = ({ link, theme = 'light', className, ...props }: CtaProps) => {
  const [isHover, setHover] = useState(false)
  const $root = useRef<HTMLAnchorElement>(null)

  useGSAP(
    () => {
      gsap.to('[data-thumb]', {
        x: isHover ? '6.3rem' : '0',
        duration: isHover ? 0.5 : 0.7,
        ease: 'power3.out'
      })
      gsap.to('[data-track]', {
        width: isHover ? '8rem' : '6rem',
        duration: isHover ? 0.5 : 0.7,
        ease: 'power3.out'
      })
    },
    { scope: $root, dependencies: [isHover] }
  )

  return (
    <Link
      href={link?.url || ''}
      className={cn('flex w-fit flex-col gap-[3.2rem]', className)}
      target={link?.target}
      {...props}
      ref={$root}
      scroll={false}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {link?.title ? (
        <Typography className="block" variant="label">
          {link.title}
        </Typography>
      ) : null}
      <span className="flex h-[1.8rem] w-[8rem] gap-[.2rem]">
        <span
          className={cn(
            'absolute z-10 flex size-[1.8rem] items-center justify-center rounded-[.8rem]',
            {
              'bg-dark-500 text-dark-100': theme === 'light',
              'bg-dark-100 text-dark-600': theme === 'dark'
            }
          )}
          data-thumb
        >
          <svg
            width="4"
            height="6"
            viewBox="0 0 4 6"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.453012 0.282602C0.676169 0.0594452 1.03798 0.0594452 1.26113 0.282602L3.54685 2.56832C3.77001 2.79147 3.77001 3.15328 3.54685 3.37644C3.32369 3.5996 2.96188 3.5996 2.73873 3.37644L0.453012 1.09072C0.229855 0.867567 0.229855 0.505759 0.453012 0.282602Z"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.453012 5.66215C0.676169 5.88531 1.03798 5.88531 1.26113 5.66215L3.54685 3.37644C3.77001 3.15328 3.77001 2.79147 3.54685 2.56832C3.32369 2.34516 2.96188 2.34516 2.73873 2.56832L0.453012 4.85403C0.229855 5.07719 0.229855 5.439 0.453012 5.66215Z"
            />
          </svg>
        </span>
        <span
          className={cn('ml-auto w-[6rem] rounded-[.8rem] bg-dark-200')}
          data-track
        ></span>
      </span>
    </Link>
  )
}

export default Cta
